import { useCallback, useEffect, useMemo, useRef } from 'react';
import { AdviseCurrentSuppliers, AdviseEngagement } from '../../../types/advise';
import { CallResult } from '../../../Api/callExternalApi';
import { isNotFoundRefusal, refusalMessage } from '../../../utils/refusalMessage';
import { useShowRefusal } from './useShowRefusal';
import { usePendingSaves } from './usePendingSaves';
import { useLatest } from '../../../utils/useLatest';
import {
  AddedBy,
  isUnanswered,
  NO_SUPPLIERS,
  REREAD_PAUSE_MS,
  withRereadAfterFailure,
  Landing
} from './currentSupplierRereads';

type AwaitOnFlush = ReturnType<typeof usePendingSaves>['awaitOnFlush'];
export type CurrentSuppliersQueue = ReturnType<typeof useCurrentSuppliersQueue>;

/** The one queue every Current Suppliers save goes through, and the copy of the step each save last left saved. */
export const useCurrentSuppliersQueue = (
  engagement: AdviseEngagement | null,
  canEdit: boolean,
  awaitOnFlush: AwaitOnFlush,
  showPatch: (patch: Partial<AdviseEngagement>) => unknown
) => {
  const engagementRef = useLatest(engagement);
  const saved = useRef(engagement?.current_suppliers ?? NO_SUPPLIERS);
  const sending = useRef<Promise<unknown>>(Promise.resolve());
  const latest = useRef(0);
  const savingFor = useRef(engagement?.id);
  const ownCopy = useRef<AdviseCurrentSuppliers | null>(null);

  // Any copy but the one this hook last showed came from the server, so it is what was saved.
  useEffect(() => {
    const opened = engagementRef.current;
    if (opened?.id !== savingFor.current) {
      savingFor.current = opened?.id;
      latest.current += 1;
      sending.current = Promise.resolve();
    } else if (opened?.current_suppliers === ownCopy.current) return;
    saved.current = opened?.current_suppliers ?? NO_SUPPLIERS;
  }, [engagement?.id, engagement?.current_suppliers]);

  const show = useCallback(
    (current: AdviseCurrentSuppliers) => {
      ownCopy.current = current;
      showPatch({ current_suppliers: current });
    },
    [showPatch]
  );

  const showRefusal = useShowRefusal();

  // One at a time, so an Undo can never reach the server before the skip it undoes, and a re-read after a
  // refusal lands before the next change is sent, so it can never overwrite that change.
  const sendInTurn = useCallback(
    (id: string, send: () => Promise<CallResult<AdviseEngagement>>, landing?: Landing) => {
      const call = sending.current.then(async () => {
        const sent = await withRereadAfterFailure(id, await send(), landing);
        // Each answer is the whole engagement, so ROI's current spend follows every saved change.
        if (!sent.error && sent.data?.current_spend && engagementRef.current?.id === id) {
          showPatch({ current_spend: sent.data.current_spend });
        }
        return sent;
      });
      sending.current = call;
      awaitOnFlush(call);
      return call;
    },
    [awaitOnFlush, engagementRef, showPatch]
  );

  // Resolves to what was sent, or null when nothing was sent or another engagement opened meanwhile.
  const sendThenShow = useCallback(
    async (send: (id: string) => Promise<CallResult<AdviseEngagement>>, landing?: Landing) => {
      const id = engagementRef.current?.id;
      if (!canEdit || !id) return null;
      const request = ++latest.current;
      const sent = await sendInTurn(id, () => send(id), landing);
      if (engagementRef.current?.id !== id) return null;
      if (!sent.error) saved.current = sent.data?.current_suppliers ?? saved.current;
      else if (sent.now) saved.current = sent.now;
      if (request === latest.current) show(saved.current);
      return sent;
    },
    [canEdit, sendInTurn, show]
  );

  const saveThenShow = useCallback(
    async (
      send: (id: string) => Promise<CallResult<AdviseEngagement>>,
      fallback: string,
      isGone?: (current: AdviseCurrentSuppliers) => boolean
    ): Promise<string | null> => {
      const sent = await sendThenShow(send);
      if (!sent?.error) return null;
      // A 404 also answers an engagement that is no longer open, so only a re-read can say the item is gone.
      if (isNotFoundRefusal(sent.error) && sent.now && isGone?.(sent.now)) return null;
      return refusalMessage(sent.error, fallback, fallback);
    },
    [sendThenShow]
  );

  // Resolves to what was sent and whether it saved, counting one with no answer as saved once a re-read shows it.
  const postThenShow = useCallback(
    async (
      send: (id: string) => Promise<CallResult<AdviseEngagement>>,
      addedBy: AddedBy,
      pauseMs = REREAD_PAUSE_MS
    ) => {
      // Read when it is sent, so what a change queued ahead of this one added is already known.
      const sentOver: { before: AdviseCurrentSuppliers | null } = { before: null };
      const sent = await sendThenShow(
        (id) => {
          sentOver.before = saved.current;
          return send(id);
        },
        { landed: (now) => !!sentOver.before && addedBy(sentOver.before, now), pauseMs }
      );
      const { before } = sentOver;
      if (!sent || !before) return null;
      const landed = !sent.error || (isUnanswered(sent.error) && !!sent.now && addedBy(before, sent.now));
      return { sent, before, landed };
    },
    [sendThenShow]
  );

  return useMemo(
    () => ({
      engagementRef,
      saved,
      latest,
      canEdit,
      show,
      showRefusal,
      sendInTurn,
      sendThenShow,
      saveThenShow,
      postThenShow
    }),
    [engagementRef, canEdit, show, showRefusal, sendInTurn, sendThenShow, saveThenShow, postThenShow]
  );
};
